/// <reference path="./markdown-it-mark.d.ts" />

/**
 * Markdown → ProseMirror JSONContent parser.
 *
 * Converts a Markdown string into an array of block-level ProseMirror
 * JSONContent nodes suitable for use inside a section node (richSection
 * or paragraphSection). This is used for:
 *
 * 1. Corruption recovery — reconstructing documentJson from Markdown
 *    typed columns when the original JSON is missing or corrupt.
 * 2. Seed data — generating initial documentJson from Markdown templates.
 *
 * Uses `markdown-it` for tokenization, then maps tokens to ProseMirror
 * JSON nodes.
 */

import {
  buildMessageMentionNodeAttrs,
  canonicalizeMessageMentionAnnotations,
  parseMessageMentionHref,
} from "@repo/shared/message-mention-link";
import type { JSONContent } from "@tiptap/react";
import MarkdownIt from "markdown-it";
import type Token from "markdown-it/lib/token.mjs";
import markdownItMark from "markdown-it-mark";
import {
  buildDocumentEvidenceNodeAttrs,
  classifyDocumentEvidenceHref,
} from "@/lib/document-evidence-link";
import { DEFAULT_HIGHLIGHT_COLOR } from "../extensions/highlight-mark";

// ─── markdown-it instance ────────────────────────────────────────────────────

const md = new MarkdownIt({
  // We want structural tokens, not HTML output
  html: false,
  // Support standard inline formatting
  typographer: false,
})
  // `==text==` → mark_open / mark_close tokens
  .use(markdownItMark);

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Parse a Markdown string into an array of ProseMirror block-level nodes.
 *
 * Returns the content array suitable for placing inside a section node.
 * An empty string produces a single empty paragraph.
 */
export function parseFromMarkdown(markdown: string): JSONContent[] {
  if (!markdown || !markdown.trim()) {
    return [{ type: "paragraph" }];
  }

  // Legacy `@Label [df-mention type=… id=…]` residue (pasted out of an older Claudia reply)
  // is repaired to the canonical mention link before tokenising so it lands as a mention node
  // instead of literal bracket text.
  const tokens = md.parse(canonicalizeMessageMentionAnnotations(markdown), {});
  const nodes = convertBlockTokens(tokens);

  // Ensure we always return at least one node (ProseMirror requires content)
  return nodes.length > 0 ? nodes : [{ type: "paragraph" }];
}

// ─── Block Token Conversion ─────────────────────────────────────────────────

/**
 * Convert a flat array of markdown-it tokens into nested ProseMirror nodes.
 *
 * markdown-it produces a flat token stream with open/close pairs:
 *   paragraph_open → inline → paragraph_close
 *   heading_open → inline → heading_close
 *   bullet_list_open → list_item_open → ... → list_item_close → bullet_list_close
 */
function convertBlockTokens(tokens: Token[]): JSONContent[] {
  const nodes: JSONContent[] = [];
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i] as Token;

    switch (token.type) {
      case "paragraph_open": {
        const [node, next] = consumeParagraph(tokens, i);
        nodes.push(node);
        i = next;
        break;
      }

      case "heading_open": {
        const [node, next] = consumeHeading(tokens, i);
        nodes.push(node);
        i = next;
        break;
      }

      case "bullet_list_open": {
        const [node, next] = consumeList(tokens, i, "bulletList");
        nodes.push(node);
        i = next;
        break;
      }

      case "ordered_list_open": {
        const [node, next] = consumeList(tokens, i, "orderedList");
        nodes.push(node);
        i = next;
        break;
      }

      case "blockquote_open": {
        const [node, next] = consumeBlockquote(tokens, i);
        nodes.push(node);
        i = next;
        break;
      }

      case "fence": {
        nodes.push(convertCodeBlock(token));
        i++;
        break;
      }

      case "code_block": {
        nodes.push(convertCodeBlock(token));
        i++;
        break;
      }

      case "hr": {
        nodes.push({ type: "horizontalRule" });
        i++;
        break;
      }

      default:
        // Skip unknown tokens (softbreak, etc.)
        i++;
        break;
    }
  }

  return nodes;
}

// ─── Block Consumers ─────────────────────────────────────────────────────────

/** Consume paragraph_open → inline → paragraph_close, return [node, nextIndex]. */
function consumeParagraph(tokens: Token[], start: number): [JSONContent, number] {
  let i = start + 1; // skip paragraph_open
  let content: JSONContent[] | undefined;

  while (i < tokens.length) {
    const tok = tokens[i] as Token;
    if (tok.type === "paragraph_close") break;
    if (tok.type === "inline") {
      content = convertInlineTokens(tok.children ?? []);
    }
    i++;
  }

  return [
    content && content.length > 0 ? { type: "paragraph", content } : { type: "paragraph" },
    i + 1, // skip paragraph_close
  ];
}

/** Consume heading_open → inline → heading_close, return [node, nextIndex]. */
function consumeHeading(tokens: Token[], start: number): [JSONContent, number] {
  const openToken = tokens[start] as Token;
  const level = Number.parseInt(openToken.tag.slice(1), 10) || 2;

  let i = start + 1;
  let content: JSONContent[] | undefined;

  while (i < tokens.length) {
    const tok = tokens[i] as Token;
    if (tok.type === "heading_close") break;
    if (tok.type === "inline") {
      content = convertInlineTokens(tok.children ?? []);
    }
    i++;
  }

  const node: JSONContent = {
    type: "heading",
    attrs: { level },
  };

  if (content && content.length > 0) {
    node.content = content;
  }

  return [node, i + 1];
}

/** Consume list_open → list_items → list_close, return [node, nextIndex]. */
function consumeList(
  tokens: Token[],
  start: number,
  listType: "bulletList" | "orderedList"
): [JSONContent, number] {
  const closeTag = listType === "bulletList" ? "bullet_list_close" : "ordered_list_close";
  let i = start + 1; // skip list_open
  const items: JSONContent[] = [];

  while (i < tokens.length) {
    const tok = tokens[i] as Token;
    if (tok.type === closeTag) break;
    if (tok.type === "list_item_open") {
      const [item, next] = consumeListItem(tokens, i);
      items.push(item);
      i = next;
    } else {
      i++;
    }
  }

  return [
    { type: listType, content: items },
    i + 1, // skip list_close
  ];
}

/** Consume list_item_open → content → list_item_close, return [node, nextIndex]. */
function consumeListItem(tokens: Token[], start: number): [JSONContent, number] {
  let i = start + 1; // skip list_item_open
  const content: JSONContent[] = [];

  while (i < tokens.length) {
    const tok = tokens[i] as Token;
    if (tok.type === "list_item_close") break;

    if (tok.type === "paragraph_open") {
      const [node, next] = consumeParagraph(tokens, i);
      content.push(node);
      i = next;
    } else if (tok.type === "bullet_list_open") {
      const [node, next] = consumeList(tokens, i, "bulletList");
      content.push(node);
      i = next;
    } else if (tok.type === "ordered_list_open") {
      const [node, next] = consumeList(tokens, i, "orderedList");
      content.push(node);
      i = next;
    } else {
      i++;
    }
  }

  return [
    {
      type: "listItem",
      content: content.length > 0 ? content : [{ type: "paragraph" }],
    },
    i + 1, // skip list_item_close
  ];
}

/** Consume blockquote_open → content → blockquote_close. */
function consumeBlockquote(tokens: Token[], start: number): [JSONContent, number] {
  let i = start + 1; // skip blockquote_open
  const innerTokens: Token[] = [];

  let depth = 1;
  while (i < tokens.length && depth > 0) {
    const tok = tokens[i] as Token;
    if (tok.type === "blockquote_open") depth++;
    if (tok.type === "blockquote_close") {
      depth--;
      if (depth === 0) break;
    }
    innerTokens.push(tok);
    i++;
  }

  const content = convertBlockTokens(innerTokens);

  return [
    {
      type: "blockquote",
      content: content.length > 0 ? content : [{ type: "paragraph" }],
    },
    i + 1, // skip blockquote_close
  ];
}

/** Convert a fenced code block or indented code block token. */
function convertCodeBlock(token: Token): JSONContent {
  const node: JSONContent = {
    type: "codeBlock",
    content: token.content ? [{ type: "text", text: token.content.replace(/\n$/, "") }] : undefined,
  };

  if (token.info) {
    node.attrs = { language: token.info.trim() };
  }

  return node;
}

// ─── Inline Token Conversion ─────────────────────────────────────────────────

/**
 * Convert markdown-it inline children to ProseMirror inline nodes.
 *
 * Inline tokens use an open/close pattern for marks (strong_open/strong_close)
 * with text tokens in between.
 */
function convertInlineTokens(children: Token[]): JSONContent[] {
  const nodes: JSONContent[] = [];
  const markStack: Array<{ type: string; attrs?: Record<string, unknown> }> = [];

  for (let index = 0; index < children.length; index++) {
    const child = children[index] as Token;

    switch (child.type) {
      // Mark opens
      case "strong_open":
        markStack.push({ type: "bold" });
        break;
      case "em_open":
        markStack.push({ type: "italic" });
        break;
      case "s_open":
        markStack.push({ type: "strike" });
        break;
      case "mark_open":
        markStack.push({ type: "highlight", attrs: { color: DEFAULT_HIGHLIGHT_COLOR } });
        break;
      case "link_open": {
        const href = child.attrGet("href") ?? "";
        const evidence = classifyDocumentEvidenceHref(href);
        if (evidence.kind !== "unrelated") {
          const [label, nextIndex] = consumeInlineLinkLabel(children, index);
          if (evidence.kind === "valid") {
            nodes.push({
              type: "documentEvidence",
              attrs: buildDocumentEvidenceNodeAttrs({
                ...evidence.reference,
                label,
              }),
            });
          } else if (label) {
            nodes.push({
              type: "text",
              text: label,
              ...(markStack.length > 0 ? { marks: [...markStack] } : {}),
            });
          }
          index = nextIndex;
          break;
        }
        const mention = parseMessageMentionHref(href);
        if (mention) {
          const [label, nextIndex] = consumeInlineLinkLabel(children, index);
          nodes.push({
            type: "mention",
            attrs: buildMessageMentionNodeAttrs({
              id: mention.id,
              label: stripLeadingMentionPrefix(label) || mention.id,
              mentionType: mention.mentionType,
              versionIntent: mention.versionIntent,
            }),
          });
          index = nextIndex;
          break;
        }
        markStack.push({ type: "link", attrs: { href } });
        break;
      }

      // Mark closes
      case "strong_close":
        removeLastMark(markStack, "bold");
        break;
      case "em_close":
        removeLastMark(markStack, "italic");
        break;
      case "s_close":
        removeLastMark(markStack, "strike");
        break;
      case "mark_close":
        removeLastMark(markStack, "highlight");
        break;
      case "link_close":
        removeLastMark(markStack, "link");
        break;

      // Inline code (self-contained — no open/close)
      case "code_inline": {
        const marks = [...markStack, { type: "code" }];
        nodes.push({
          type: "text",
          text: child.content,
          marks: marks.length > 0 ? marks : undefined,
        });
        break;
      }

      // Plain text
      case "text": {
        if (child.content) {
          const node: JSONContent = { type: "text", text: child.content };
          if (markStack.length > 0) {
            node.marks = [...markStack];
          }
          nodes.push(node);
        }
        break;
      }

      // Line break
      case "softbreak":
        // Soft breaks become spaces in output (Markdown default)
        nodes.push({ type: "text", text: " " });
        break;

      case "hardbreak":
        nodes.push({ type: "hardBreak" });
        break;
    }
  }

  return nodes;
}

function consumeInlineLinkLabel(children: Token[], startIndex: number): [string, number] {
  let depth = 1;
  let label = "";
  let index = startIndex + 1;

  while (index < children.length) {
    const child = children[index] as Token;
    if (child.type === "link_open") {
      depth++;
      index++;
      continue;
    }
    if (child.type === "link_close") {
      depth--;
      if (depth === 0) {
        return [label, index];
      }
      index++;
      continue;
    }
    if (depth === 1) {
      if (child.type === "text" || child.type === "code_inline") {
        label += child.content;
      } else if (child.type === "softbreak" || child.type === "hardbreak") {
        label += " ";
      }
    }
    index++;
  }

  return [label, children.length - 1];
}

function stripLeadingMentionPrefix(label: string): string {
  return label.trim().replace(/^@/, "").trim();
}

/** Remove the last occurrence of a mark type from the stack. */
function removeLastMark(
  stack: Array<{ type: string; attrs?: Record<string, unknown> }>,
  type: string
): void {
  for (let i = stack.length - 1; i >= 0; i--) {
    if ((stack[i] as (typeof stack)[number]).type === type) {
      stack.splice(i, 1);
      return;
    }
  }
}
